import React, { createContext, FC, useContext, useState } from 'react';
import { HealthCheckStatus } from 'models/healthCheck';
import { useHealthCheck } from 'hooks/useHealthCheck';

interface SystemStatusContextValue {
  showBanner: boolean;
  failureReasons: string[];
  dismiss: () => void;
}

const SystemStatusContext = createContext<SystemStatusContextValue>({
  showBanner: false,
  failureReasons: [],
  dismiss: () => undefined,
});

const SERVICE_LABELS: Record<string, string> = {
  db: 'Database',
  'git-garage': 'Git',
};

const formatServiceName = (availableKey: string): string => {
  const name = availableKey.replace(/-available$/, '');
  return SERVICE_LABELS[name] ?? name;
};

const extractFailureReasons = (
  data: HealthCheckStatus | undefined
): string[] => {
  if (!data) return [];

  // Explicit reasons are only written on timeout or unexpected future exception
  const explicitReasons = Object.entries(data)
    .filter(([key]) => /^failure-reason-\d+$/.test(key))
    .sort(([a], [b]) => {
      const indexA = parseInt(a.replace('failure-reason-', ''), 10);
      const indexB = parseInt(b.replace('failure-reason-', ''), 10);
      return indexA - indexB;
    })
    .map(([, value]) => String(value));

  if (explicitReasons.length > 0) {
    return explicitReasons;
  }

  // Fall back: list which services reported themselves as unavailable
  return Object.entries(data)
    .filter(([key, value]) => key.endsWith('-available') && value === 'false')
    .map(([key]) => `${formatServiceName(key)} is unavailable`);
};

const useSystemStatusProvider = (): SystemStatusContextValue => {
  const { data, isError, dataUpdatedAt, errorUpdatedAt } = useHealthCheck();
  const [dismissedUntil, setDismissedUntil] = useState(0);

  const isUnavailable = isError || data?.failure !== undefined;
  const lastUpdateTime = Math.max(dataUpdatedAt, errorUpdatedAt);
  const showBanner = isUnavailable && lastUpdateTime > dismissedUntil;
  const failureReasons = isError
    ? ['Unable to reach backend services.']
    : extractFailureReasons(data);

  const dismiss = () => setDismissedUntil(lastUpdateTime);

  return { showBanner, failureReasons, dismiss };
};

export const SystemStatusProvider: FC<{}> = ({ children }) => {
  const value = useSystemStatusProvider();
  return (
    <SystemStatusContext.Provider value={value}>
      {children}
    </SystemStatusContext.Provider>
  );
};

export const useSystemStatus = () => useContext(SystemStatusContext);
